import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import {
  sendOtp as apiSendOtp,
  verifyOtp as apiVerifyOtp,
  loadAuthSession,
  getCafeMenuProfile,
  saveAuthSession,
  clearAuthSession,
  SESSION_TTL_MS,
  REWARD_REVERIFY_AFTER_MS,
} from "../cafemenuApi";
import { useBilling } from "./BillingContext";
import { cacheClear } from "../utils/sessionCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Rewards need both the backend billingEnable flag and the ?allowBilling param.
  const { billingAllowed } = useBilling();
  const [session, setSession] = useState(() => loadAuthSession());
  // Set when a sensitive action (spending Circle points) needs a fresh OTP
  // even though the session itself is still valid.
  const [reauthRequired, setReauthRequired] = useState(false);
  // >0 while something (a payment in flight) must not lose its login to the
  // expiry timer.
  const holdCountRef = useRef(0);

  const sendOtp = useCallback(async (phoneNumber) => {
    return apiSendOtp(phoneNumber);
  }, []);

  const verifyOtp = useCallback(async (phoneNumber, code) => {
    const data = await apiVerifyOtp(phoneNumber, code);
    const nextSession = {
      token: data.access_token,
      userId: data.user?.id,
      phoneNumber: data.user?.nationalNumber || `+91${phoneNumber}`,
      // Shown in the menu's account chip; the photo (and any newer name) is
      // filled in by the profile fetch below.
      name: data.user?.name || "",
      imageUrl: "",
      profileLoaded: false,
      verifiedAt: Date.now(),
    };
    setSession(nextSession);
    setReauthRequired(false);
    saveAuthSession(nextSession);
    return nextSession;
  }, []);

  const logout = useCallback(() => {
    cacheClear();
    setSession(null);
    setReauthRequired(false);
    clearAuthSession();
  }, []);

  const requestReauth = useCallback(() => setReauthRequired(true), []);
  const cancelReauth = useCallback(() => setReauthRequired(false), []);

  const isRecentlyVerified = useCallback(
    () => Boolean(session?.verifiedAt) && Date.now() - session.verifiedAt < REWARD_REVERIFY_AFTER_MS,
    [session]
  );

  // Pause expiry while a payment is being made/confirmed. Returns a release
  // function that is safe to call more than once.
  const holdSession = useCallback(() => {
    holdCountRef.current += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      holdCountRef.current = Math.max(0, holdCountRef.current - 1);
    };
  }, []);

  // cafemenuApi's graphqlRequest clears localStorage itself the moment a
  // stored token turns out invalid (it has no access to this React state) —
  // this is what actually flips isAuthenticated back to false so the UI can
  // react instead of staying stuck on "verified" forever.
  useEffect(() => {
    const onAuthExpired = () => {
      cacheClear();
      setSession(null);
    };
    window.addEventListener("cafemenu-auth-expired", onAuthExpired);
    return () => window.removeEventListener("cafemenu-auth-expired", onAuthExpired);
  }, []);

  // Expire the visit-scoped session on time, but never in the middle of a
  // payment — retry shortly after the hold is released instead.
  const verifiedAt = session?.verifiedAt;
  useEffect(() => {
    if (!verifiedAt) return undefined;
    let timer;
    const schedule = (ms) => {
      timer = setTimeout(() => {
        if (holdCountRef.current > 0) {
          schedule(15000);
          return;
        }
        setSession(null);
        clearAuthSession();
      }, ms);
    };
    schedule(Math.max(1000, verifiedAt + SESSION_TTL_MS - Date.now()));
    return () => clearTimeout(timer);
  }, [verifiedAt]);

  // Load the account's name + photo once per login (best effort — the chip
  // falls back to initials, so a failure here is never user-visible).
  const profileToken = session?.token;
  const profileLoaded = session?.profileLoaded;
  useEffect(() => {
    if (!billingAllowed || !profileToken || profileLoaded) return undefined;
    let cancelled = false;
    getCafeMenuProfile(profileToken)
      .then((profile) => {
        if (cancelled) return;
        setSession((current) => {
          if (!current || current.token !== profileToken) return current;
          const next = {
            ...current,
            name: profile.name || current.name || "",
            imageUrl: profile.imageUrl || "",
            profileLoaded: true,
          };
          saveAuthSession(next);
          return next;
        });
      })
      .catch(() => { /* chip keeps showing initials */ });
    return () => { cancelled = true; };
  }, [billingAllowed, profileToken, profileLoaded]);

  const value = {
    isAuthenticated: Boolean(session?.token),
    name: session?.name || "",
    imageUrl: session?.imageUrl || "",
    token: session?.token || null,
    userId: session?.userId || null,
    phoneNumber: session?.phoneNumber || null,
    verifiedAt: session?.verifiedAt || null,
    reauthRequired,
    requestReauth,
    cancelReauth,
    isRecentlyVerified,
    holdSession,
    sendOtp,
    verifyOtp,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
